import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState, useCallback } from 'react';
import { CACHE_KEYS } from '@/lib/cacheUtils';

export interface GlobalAnnouncement {
  id: string;
  title: string;
  message: string | null;
  link: string | null;
  link_text: string | null;
  variant: 'info' | 'warning' | 'success' | 'promo';
  is_active: boolean;
  is_dismissible: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const DISMISSED_KEY = CACHE_KEYS.DISMISSED_ANNOUNCEMENTS;

function getDismissedIds(): string[] {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setDismissedIds(ids: string[]) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
}

export function useGlobalAnnouncements() {
  const queryClient = useQueryClient();
  const [dismissedIds, setDismissedIdsState] = useState<string[]>(getDismissedIds);

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['global-announcements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('global_announcements')
        .select('*')
        .eq('is_active', true)
        .or('starts_at.is.null,starts_at.lte.now()')
        .or('ends_at.is.null,ends_at.gt.now()')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as GlobalAnnouncement[];
    },
    staleTime: 1000 * 60, // 1 minute
  });

  // Filter out dismissed announcements
  const visibleAnnouncements = announcements.filter(
    (a) => !dismissedIds.includes(a.id)
  );

  const dismissAnnouncement = useCallback((id: string) => {
    const newDismissed = [...dismissedIds, id];
    setDismissedIds(newDismissed);
    setDismissedIdsState(newDismissed);
  }, [dismissedIds]);

  // Real-time subscription for live updates
  useEffect(() => {
    const channel = supabase
      .channel('global-announcements-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'global_announcements',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['global-announcements'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    announcements: visibleAnnouncements,
    allAnnouncements: announcements,
    isLoading,
    dismissAnnouncement,
    dismissedIds,
  };
}

// Admin hook to fetch ALL announcements (not just active)
export function useAdminGlobalAnnouncements() {
  const queryClient = useQueryClient();

  const { data: announcements = [], isLoading, refetch } = useQuery({
    queryKey: ['admin-global-announcements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('global_announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as GlobalAnnouncement[];
    },
  });

  const createAnnouncement = async (announcement: Omit<Partial<GlobalAnnouncement>, 'id' | 'created_at' | 'updated_at'> & { title: string }) => {
    const { error } = await supabase
      .from('global_announcements')
      .insert([announcement]);
    
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ['admin-global-announcements'] });
    queryClient.invalidateQueries({ queryKey: ['global-announcements'] });
  };

  const updateAnnouncement = async (id: string, updates: Partial<GlobalAnnouncement>) => {
    const { error } = await supabase
      .from('global_announcements')
      .update(updates)
      .eq('id', id);
    
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ['admin-global-announcements'] });
    queryClient.invalidateQueries({ queryKey: ['global-announcements'] });
  };

  const deleteAnnouncement = async (id: string) => {
    const { error } = await supabase
      .from('global_announcements')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ['admin-global-announcements'] });
    queryClient.invalidateQueries({ queryKey: ['global-announcements'] });
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    await updateAnnouncement(id, { is_active: isActive });
  };

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('admin-global-announcements-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'global_announcements',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['admin-global-announcements'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    announcements,
    isLoading,
    refetch,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    toggleActive,
  };
}
